"use client"

import * as React from "react"
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import { XIcon } from "lucide-react"

import { cn } from "@/lib/utils"

type DialogDismissHandlers = {
  onEscapeKeyDown?: (event: KeyboardEvent) => void
  onInteractOutside?: (event: Event) => void
}

const DialogDismissContext = React.createContext<{
  handlersRef: React.MutableRefObject<DialogDismissHandlers | null>
} | null>(null)

type DialogProps = Omit<React.ComponentProps<typeof DialogPrimitive.Root>, "children" | "onOpenChange"> & {
  children?: React.ReactNode
  onOpenChange?: (open: boolean) => void
}

function Dialog({
  children,
  onOpenChange,
  ...props
}: DialogProps) {
  const handlersRef = React.useRef<DialogDismissHandlers | null>(null)

  const handleOpenChange = React.useCallback(
    (open: boolean, eventDetails: Parameters<NonNullable<React.ComponentProps<typeof DialogPrimitive.Root>["onOpenChange"]>>[1]) => {
      if (!open) {
        if (eventDetails.reason === "outside-press") {
          const onInteractOutside = handlersRef.current?.onInteractOutside
          if (onInteractOutside) {
            onInteractOutside(eventDetails.event)
            eventDetails.cancel()
            return
          }
        }

        if (eventDetails.reason === "escape-key") {
          const onEscapeKeyDown = handlersRef.current?.onEscapeKeyDown
          if (onEscapeKeyDown) {
            onEscapeKeyDown(eventDetails.event)
            eventDetails.cancel()
            return
          }
        }
      }

      onOpenChange?.(open)
    },
    [onOpenChange]
  )

  return (
    <DialogDismissContext.Provider value={{ handlersRef }}>
      <DialogPrimitive.Root data-slot="dialog" onOpenChange={handleOpenChange} {...props}>
        {children}
      </DialogPrimitive.Root>
    </DialogDismissContext.Provider>
  )
}

function DialogTrigger({
  asChild,
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }) {
  if (asChild && React.isValidElement(children)) {
    return (
      <DialogPrimitive.Trigger
        data-slot="dialog-trigger"
        className={className}
        render={children as React.ReactElement}
        {...(props as any)}
      />
    )
  }

  return (
    <DialogPrimitive.Trigger
      data-slot="dialog-trigger"
      className={className}
      {...(props as any)}
    >
      {children}
    </DialogPrimitive.Trigger>
  )
}

function DialogPortal(props: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

const DialogClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }
>(({ asChild, children, className, ...props }, ref) => {
  if (asChild && React.isValidElement(children)) {
    return (
      <DialogPrimitive.Close
        ref={ref}
        data-slot="dialog-close"
        className={className}
        render={children as React.ReactElement}
        {...(props as any)}
      />
    )
  }

  return (
    <DialogPrimitive.Close
      ref={ref}
      data-slot="dialog-close"
      className={className}
      {...(props as any)}
    >
      {children}
    </DialogPrimitive.Close>
  )
})
DialogClose.displayName = "DialogClose"

const DialogOverlay = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof DialogPrimitive.Backdrop>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Backdrop
      ref={ref}
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/50 transition-all duration-200",
        "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
        className
      )}
      {...props}
    />
  )
})
DialogOverlay.displayName = "DialogOverlay"

interface DialogContentProps extends React.ComponentProps<typeof DialogPrimitive.Popup> {
  showCloseButton?: boolean
  onEscapeKeyDown?: (event: KeyboardEvent) => void
  onInteractOutside?: (event: Event) => void
}

const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(({
  className,
  children,
  showCloseButton = true,
  onEscapeKeyDown,
  onInteractOutside,
  ...props
}, ref) => {
  const dismissContext = React.useContext(DialogDismissContext)

  React.useEffect(() => {
    if (!dismissContext) {
      return
    }

    dismissContext.handlersRef.current = {
      onEscapeKeyDown,
      onInteractOutside,
    }

    return () => {
      if (
        dismissContext.handlersRef.current?.onEscapeKeyDown === onEscapeKeyDown &&
        dismissContext.handlersRef.current?.onInteractOutside === onInteractOutside
      ) {
        dismissContext.handlersRef.current = null
      }
    }
  }, [dismissContext, onEscapeKeyDown, onInteractOutside])

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        ref={ref}
        data-slot="dialog-content"
        className={cn(
          "bg-background fixed top-[50%] left-[50%] z-[51] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          "data-[starting-style]:fade-in-0 data-[starting-style]:zoom-in-95",
          "data-[ending-style]:fade-out-0 data-[ending-style]:zoom-out-95",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            className="ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
})
DialogContent.displayName = "DialogContent"

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    />
  )
}

const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Title
      ref={ref}
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...(props as any)}
    />
  )
})
DialogTitle.displayName = "DialogTitle"

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Description
      ref={ref}
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...(props as any)}
    />
  )
})
DialogDescription.displayName = "DialogDescription"

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
